import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';
import * as memberAPI from '../../lib/api/member';

// 모달(+무거운 의존성)은 실제 스캔 성공 시에만 로드 → 로그인 등 유휴 페이지 부담 최소화
const MemberManageModal = lazy(() => import('./MemberManageModal'));

/**
 * 전역 바코드 스캐너 리스너. USB 스캐너 2가지 연결 방식을 지원:
 *   1) 키보드웨지(HID Keyboard) : 이 웹 화면이 앞(활성)일 때 동작. 연결 불필요.
 *   2) WebSerial(VCOM)        : 설정에서 연결. 다른 창 포커스 중에도 수신(Chrome/Edge).
 * 두 입력원 모두 handleScanned 로 진입한다.
 *
 * barcodeMode(admin 클럽설정 > 바코드 스캔모드)에 따라 분기:
 *   0 회원 스캐너(기본, → MemberManageModal) / 1 미사용 / 2 출석기(Phase C 예정)
 * 비상용 QR = 1000000000 + playerID.
 */
const MAX_GAP_MS = 50; // 이 간격보다 느리면 사람 타이핑 → 버퍼 리셋
const QR_BASE = 1000000000; // 비상용 QR = QR_BASE + playerID

const MODE_UNUSED = 1; // 0=회원 스캐너(기본), 1=미사용, 2=출석기
const MODE_ATTEND = 2;

// 비상용 QR 패턴: 정확히 10자리 숫자(선행 0 없음) & 1e9 초과 → 1000000000 + playerID.
// 이름/전화번호 검색창 등 입력창에 포커스가 있어도 이 패턴이면 검색 입력을 무시하고 스캔으로 처리한다.
// (사람이 이름/전화번호를 타이핑하는 건 키 간격이 느려 버퍼가 리셋되므로 이 패턴에 걸리지 않음)
const isEmergencyQr = (s) => /^[1-9][0-9]{9}$/.test(s) && Number(s) > QR_BASE;

// 스캔값이 찍힌 입력창(검색창 등)을 비운다 — 제어 컴포넌트도 초기화되도록 native setter + input 이벤트 사용
const clearScannedInput = (el) => {
  try {
    if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return;
    const proto = el.tagName === 'INPUT' ? window.HTMLInputElement.prototype : window.HTMLTextAreaElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
    setter.call(el, '');
    el.dispatchEvent(new Event('input', { bubbles: true }));
  } catch (e) {}
};

const QrScanListener = () => {
  const [target, setTarget] = useState(null); // { playerID, member } | null
  const buffer = useRef('');
  const lastTime = useRef(0);
  const inFlight = useRef(false); // 회원 조회 진행 중 → 중복 스캔 무시(레이스 가드)
  const [connectNonce, setConnectNonce] = useState(0); // 설정에서 스캐너 연결 시 WebSerial 재연결 트리거

  const { clubID, socketKey, faceActive, adminPlayerID, barcodeMode } = useSelector(({ club, socket, user }) => ({
    clubID: club.defaultClub && club.defaultClub.clubID,
    socketKey: socket.socketKey,
    faceActive: club.defaultClub && club.defaultClub.faceActive,
    adminPlayerID: user.user && user.user.playerID,
    barcodeMode: club.defaultClub ? Number(club.defaultClub.barcodeMode) || 0 : 0,
  }));
  // 클로저에서 항상 최신값을 쓰도록 ref 동기화
  const ctx = useRef({ clubID, socketKey, faceActive, adminPlayerID, barcodeMode });
  ctx.current = { clubID, socketKey, faceActive, adminPlayerID, barcodeMode };
  const modalOpen = useRef(false);
  modalOpen.current = !!target;

  // 디코드 + 모드별 라우팅 (세 입력원 공통 진입점)
  const handleScanned = useCallback(async (scanned) => {
    const { clubID, socketKey, barcodeMode } = ctx.current;
    if (!clubID || !socketKey) return; // 로그인/클럽선택 안됨
    if (barcodeMode === MODE_UNUSED) return; // 미사용
    // 비상용 QR = 순수 숫자 + 10자리 이하(QR_BASE ~ QR_BASE*10 미만) → playerID 범위 (0, 9e9).
    // PLAYER_ID(uint, ~4.29e9) 전 범위 커버하면서, EAN-13(13자리)/UPC-A(12자리) 등 상품 바코드와
    // hex/지수표기/부호/공백 형식을 차단.
    if (!/^[0-9]+$/.test(scanned)) return;
    const num = Number(scanned);
    if (!Number.isInteger(num) || num <= QR_BASE || num >= QR_BASE * 10) return;
    const playerID = num - QR_BASE;

    if (barcodeMode === MODE_ATTEND) {
      // TODO(Phase C): kioskAttendSelect → 출석 처리 + 5초 결과 모달 + 만료 경고
      alert('출석기 모드는 다음 단계(Phase C)에서 구현됩니다. (playerID ' + playerID + ')');
      return;
    }

    // 회원 스캐너 모드
    if (modalOpen.current || inFlight.current) return; // 모달 열림/조회 중이면 무시
    inFlight.current = true;
    try {
      const res = await memberAPI.read({ clubID, targetID: playerID, socketKey });
      const data = res && res.data;
      if (!data || data.inClub !== true || !data.user || !data.user.info) {
        alert('내 클럽 회원이 아닙니다.');
        return;
      }
      setTarget({ playerID, member: data.user.info });
    } catch (e) {
      alert('회원 조회에 실패했습니다.');
    } finally {
      inFlight.current = false;
    }
  }, []);

  // 입력원 1: 키보드웨지 (HID Keyboard) — 이 웹 화면이 포커스일 때
  useEffect(() => {
    const onKeyDown = (e) => {
      if (ctx.current.barcodeMode === MODE_UNUSED) return; // 미사용
      if (e.isComposing) return; // IME 조합 중 무시
      const now = Date.now();
      if (now - lastTime.current > MAX_GAP_MS) buffer.current = ''; // 사람 타이핑(느림) → 리셋
      lastTime.current = now;
      if (e.key === 'Enter') {
        const scanned = buffer.current;
        buffer.current = '';
        // 검색창 등 입력창에 포커스가 있어도, 비상용 QR 패턴이면 검색 입력 무시하고 스캔으로 처리
        if (isEmergencyQr(scanned)) {
          e.preventDefault();
          clearScannedInput(e.target);
          handleScanned(scanned);
        }
      } else if (e.key && e.key.length === 1) {
        buffer.current += e.key;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleScanned]);

  // 설정에서 "스캐너 연결" 시 → 새로고침 없이 WebSerial 재연결(아래 effect 재실행)
  useEffect(() => {
    const onConnected = () => setConnectNonce((n) => n + 1);
    window.addEventListener('barcode-scanner-connected', onConnected);
    return () => window.removeEventListener('barcode-scanner-connected', onConnected);
  }, []);

  // 입력원 2: WebSerial (VCOM) — 설정에서 연결(권한 부여)된 시리얼 스캐너
  useEffect(() => {
    if (barcodeMode === MODE_UNUSED) return;
    if (typeof navigator === 'undefined' || !navigator.serial) return;

    let port = null;
    let reader = null;
    let cancelled = false;
    let line = '';

    const readLoop = async () => {
      try {
        reader = port.readable.getReader();
        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          if (!value) continue;
          for (let i = 0; i < value.length; i++) {
            const b = value[i];
            if (b === 0x0d || b === 0x0a) {
              if (line) {
                handleScanned(line);
                line = '';
              }
            } else if (b >= 0x20 && b <= 0x7e) {
              line += String.fromCharCode(b);
            }
          }
        }
      } catch (e) {
        // 읽기 중단/에러
      } finally {
        try {
          if (reader) reader.releaseLock();
        } catch (e) {}
      }
    };

    (async () => {
      try {
        const ports = await navigator.serial.getPorts();
        if (cancelled || !ports || ports.length === 0) return; // 미연결 → 다른 입력원 사용
        port = ports[0];
        await port.open({ baudRate: 9600 }); // VCOM 은 보통 baud 무관. 안 맞으면 스캐너 설정에 맞춰 조정
        readLoop();
      } catch (e) {
        // 권한 없음/이미 열림/열기 실패 → 폴백
      }
    })();

    return () => {
      cancelled = true;
      if (reader) {
        try {
          reader.cancel();
        } catch (e) {}
      }
      if (port) {
        setTimeout(() => {
          try {
            port.close();
          } catch (e) {}
        }, 0);
      }
    };
  }, [barcodeMode, handleScanned, connectNonce]);

  if (!target) return null;
  return (
    <Suspense fallback={null}>
      <MemberManageModal
        playerID={target.playerID}
        clubID={ctx.current.clubID}
        socketKey={ctx.current.socketKey}
        faceActive={ctx.current.faceActive}
        adminPlayerID={ctx.current.adminPlayerID}
        member={target.member}
        onClose={() => setTarget(null)}
      />
    </Suspense>
  );
};

export default QrScanListener;
